import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Archive,
  Bookmark,
  Building2,
  ChevronDown,
  ChevronRight,
  Columns3,
  FileText,
  FolderClosed,
  FolderPlus,
  Import,
  LayoutDashboard,
  LayoutGrid,
  ListChecks,
  ListPlus,
  Lock,
  PanelLeftClose,
  Pencil,
  Plus,
  Rows3,
  Search,
  Settings,
  Trash2,
  Users,
} from 'lucide-react';
import { api, type Session } from '../lib/api';
import { useRefreshSession } from '../lib/session';
import { buildNavTree, rememberOrg, useSpaces, type NavSpace } from '../lib/spaces';
import { Kbd, Logo, Menu, MOD, Spinner, Tip } from '@vatman/ui';
import { openQuickOpen } from './QuickOpen';
import { NavRowMenu, useRowMenu, type RowAction } from './NavRowMenu';
import { favoriteLink, reorderFavorites, useFavorites, type Favorite } from '../lib/favorites';
import { create } from 'zustand';
import { hideNav } from './navState';
import { InboxNavItem } from './inbox/InboxNavItem';
import { UserMenu } from './dashboard/Dashboard';
import { NameDialog } from './dashboard/parts';
import { CreateDialogs, openCreate, useHere } from './spaces/dialogs';
import { CreateMenu } from './CreateMenu';
import { useArchivedProjects, useProjects, useTeamSprints, type TrackerProject } from './tracker/model';
import { projectLink } from '../lib/recent';
import { createPage, movePage, pagePath, pageTitle, refreshPages, usePageLevel, type Page } from '../docs/model';
import { closeDocsImport, openDocsImport, useDocsImport } from '../docs/importOpen';
import { ApprovalBadge } from '../docs/ApprovalBadge';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@vatman/ui';
import { errorText } from '../i18n';
import { ApiError, type Space } from '../lib/api';
import { track } from '../lib/track';
import '../shell.css';
import './workspaceNav.css';
import { SpaceIcon } from './spaces/SpaceIcon';

const ImportDialog = lazy(() => import('./tracker/ImportDialog').then((m) => ({ default: m.ImportDialog })));
const DocsImportDialog = lazy(() => import('../docs/ImportDialog').then((m) => ({ default: m.DocsImportDialog })));

/** Board views of the dashboard (`/?view=`). */
export type BoardView = 'all' | 'recent' | 'starred' | 'mine' | 'shared' | 'projects' | 'trash' | 'org';

/** What the page shows, so its row is highlighted. */
export type NavActive =
  | { kind: 'view'; view: BoardView }
  | { kind: 'space'; id: string; tab?: 'boards' | 'tasks' | 'docs' | 'news' }
  /** A page of the documents: `path` is the pages from the top of its tree down to it. */
  | { kind: 'page'; id: string; spaceId: string; path: string[] }
  | { kind: 'project'; id: string; view?: string | null }
  | { kind: 'myIssues' }
  | { kind: 'inbox' }
  /** A team's page (docs/teams-plan.md): its board, backlog and the rest. */
  | { kind: 'team'; id: string; tab?: string }
  | { kind: 'filter'; id: string }
  | { kind: 'none' };

const ORG_COLORS = ['#5B4CF0', '#0090FF', '#12A594', '#F76B15', '#D6409F', '#30A46C'];
export const orgColor = (id: string) => ORG_COLORS[parseInt(id.replace(/-/g, '').slice(-4), 16) % ORG_COLORS.length];

/*
 * Branches of the tree the person opened or closed, remembered in this browser. A branch holding
 * the page in view opens by itself unless it was closed on purpose. Ids: a space's id, `p:` and a
 * project's id, `team:` and a team's id.
 */
const OPEN_KEY = 'vatman.nav.open';
interface Branches {
  open: string[];
  shut: string[];
}
function loadBranches(): Branches {
  const ids = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string').slice(0, 300) : []);
  try {
    const v = JSON.parse(localStorage.getItem(OPEN_KEY) ?? '{}');
    // Before: a list of open spaces, with `team:` ids for folded teams.
    if (Array.isArray(v)) {
      const list = ids(v);
      return { open: list.filter((x) => !x.startsWith('team:')), shut: list.filter((x) => x.startsWith('team:')) };
    }
    return { open: ids(v?.open), shut: ids(v?.shut) };
  } catch {
    return { open: [], shut: [] };
  }
}
function saveBranches(b: Branches) {
  try {
    localStorage.setItem(OPEN_KEY, JSON.stringify(b));
  } catch {
    // Not critical: the tree just starts as it is next time.
  }
}

/** Open or closed: a branch closed on purpose stays closed, one holding the page opens by itself. */
type Tree = { isOpen: (id: string, holdsActive: boolean, byDefault?: boolean) => boolean; toggle: (id: string, now: boolean) => void };

/**
 * The sidebar of the whole app (docs/navigation-plan.md): the organization, "Create" and search,
 * what is mine, the spaces with their boards and projects, and everything in the organization.
 * A person alone sees their spaces at once; a company sees its teams, each with its spaces.
 */
export function WorkspaceNav({ session, org, active }: { session: Session; org: string | null; active: NavActive }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const refresh = useRefreshSession();
  const current = session.orgs.find((o) => o.id === org) ?? null;
  const spaces = useSpaces(org);
  const projects = useProjects();
  const teamSprints = useTeamSprints();
  const tree = useMemo(() => buildNavTree(org, session.teams, session.orgs, spaces.data ?? [], (projects.data ?? []).filter((p) => !p.archived)), [org, session, spaces.data, projects.data]);
  // New things go where the person is: the team, space and project of the page in view.
  const here = useMemo(() => {
    // A team's page: sprints made here are the team's.
    if (active.kind === 'team') return { team: active.id, space: null, project: null };
    if (active.kind === 'space') {
      const s = spaces.data?.find((x) => x.id === active.id);
      return s ? { team: s.teamId, space: s.id, project: null } : null;
    }
    if (active.kind === 'page') {
      const s = spaces.data?.find((x) => x.id === active.spaceId);
      return s ? { team: s.teamId, space: s.id, project: null } : null;
    }
    if (active.kind === 'project') {
      const p = projects.data?.find((x) => x.id === active.id);
      if (!p) return null;
      // A project seen through the organization or as a guest is not a place to create boards in.
      const mine = session.teams.some((tm) => tm.id === p.teamId);
      return { team: mine ? p.teamId : null, space: mine ? p.spaceId : null, project: p.id };
    }
    return null;
  }, [active, spaces.data, projects.data, session.teams]);
  // Someone alone has one space: it is open unless closed, the projects in it are what they came for.
  const single = tree.groups.reduce((n, g) => n + g.spaces.length, 0) === 1;
  useEffect(() => {
    useHere.setState({ org, team: here?.team ?? null, space: here?.space ?? null, project: here?.project ?? null });
  }, [org, here]);

  const [branches, setBranches] = useState(loadBranches);
  const branchTree: Tree = {
    isOpen: (id, holdsActive, byDefault = false) => !branches.shut.includes(id) && (byDefault || holdsActive || branches.open.includes(id)),
    toggle: (id, now) => {
      const next = now ? { open: branches.open.filter((x) => x !== id), shut: [...branches.shut.filter((x) => x !== id), id] } : { open: [...branches.open.filter((x) => x !== id), id], shut: branches.shut.filter((x) => x !== id) };
      setBranches(next);
      saveBranches(next);
    },
  };

  // The row of the page in view stays in sight in a long tree.
  const sideRef = useRef<HTMLElement>(null);
  const activeKey = JSON.stringify(active);
  useEffect(() => {
    const el = sideRef.current?.querySelector<HTMLElement>('.nav-tree [data-active="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeKey, spaces.data, projects.data]);

  // ↑↓ through the rows, → and ← open and close a branch; Enter follows the link.
  const treeKeys = (e: React.KeyboardEvent) => {
    const el = e.target as HTMLElement;
    if (!el.matches('a.nav-item, a.nav-team') || e.altKey || e.metaKey || e.ctrlKey) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const rows = [...(sideRef.current?.querySelectorAll<HTMLElement>('a.nav-item, a.nav-team') ?? [])].filter((x) => x.offsetParent !== null);
      const next = rows[rows.indexOf(el) + (e.key === 'ArrowDown' ? 1 : -1)];
      if (next) {
        e.preventDefault();
        next.focus();
      }
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      const toggle = el.parentElement?.querySelector<HTMLButtonElement>(':scope > .nav-toggle[aria-expanded], :scope > .nav-team-toggle');
      if (toggle && (toggle.getAttribute('aria-expanded') === 'true') !== (e.key === 'ArrowRight')) {
        e.preventDefault();
        toggle.click();
      }
    }
  };

  const [newOrg, setNewOrg] = useState(false);
  const [importing, setImporting] = useState(false);
  const docsImport = useDocsImport((s) => s.target);
  const orgQuery = org ? `org=${org}` : '';
  const viewLink = (view: BoardView) => {
    const p = new URLSearchParams(orgQuery);
    if (view !== 'all') p.set('view', view);
    const s = p.toString();
    return s ? `/?${s}` : '/';
  };
  const switchOrg = (id: string | null) => {
    rememberOrg(id);
    navigate(id ? `/?org=${id}` : '/');
  };
  const isView = (...v: BoardView[]) => active.kind === 'view' && v.includes(active.view);
  const orgIcon = (id: string, small?: boolean) => (
    <span className={`ws-icon${small ? ' sm' : ''}`} style={{ background: orgColor(id) }}>
      {session.orgs.find((o) => o.id === id)?.name.slice(0, 1).toUpperCase()}
    </span>
  );

  return (
    <aside className="side" ref={sideRef} onKeyDown={treeKeys}>
      <div className="side-head">
      <Menu.Root>
        <Menu.Trigger asChild>
          <button className="ws-switch">
            {current ? orgIcon(current.id) : <Logo size={32} />}
            <span style={{ minWidth: 0, flex: 1 }}>
              <div className="ws-name">{current ? current.name : t('nav.personal')}</div>
              <div className="ws-sub" title={current ? undefined : session.user!.email}>
                {current ? t(`org.roles.${current.role}`) : session.user!.email}
              </div>
            </span>
            <ChevronDown size={16} color="var(--text-3)" />
          </button>
        </Menu.Trigger>
        <Menu.Content>
          <Menu.Item icon={<Logo size={20} />} onSelect={() => switchOrg(null)} keys={!current ? ['✓'] : undefined}>
            {t('nav.personal')}
          </Menu.Item>
          {session.orgs.map((o) => (
            <Menu.Item key={o.id} icon={orgIcon(o.id, true)} onSelect={() => switchOrg(o.id)} keys={o.id === current?.id ? ['✓'] : undefined}>
              {o.name}
            </Menu.Item>
          ))}
          <Menu.Item icon={<Plus size={16} />} onSelect={() => setNewOrg(true)}>
            {t('nav.newOrg')}
          </Menu.Item>
          <Menu.Separator />
          {current && (
            <>
              <Menu.Item icon={<Settings size={16} />} onSelect={() => navigate(`/?org=${current.id}&view=org`)}>
                {t('nav.orgSettings')}
              </Menu.Item>
              <Menu.Item icon={<Users size={16} />} onSelect={() => navigate(`/?org=${current.id}&view=org#members`)}>
                {t('nav.members')}
              </Menu.Item>
            </>
          )}
          <Menu.Item icon={<Import size={16} />} onSelect={() => navigate(`/?${orgQuery ? `${orgQuery}&` : ''}import=miro`)}>
            {t('nav.importMiro')}
          </Menu.Item>
          <Menu.Item
            icon={<Import size={16} />}
            onSelect={() => {
              track('tracker_import_opened');
              setImporting(true);
            }}
          >
            {t('nav.importIssues')}
          </Menu.Item>
          <Menu.Item
            icon={<Import size={16} />}
            onSelect={() => {
              track('docs_import_opened');
              openDocsImport({ teamId: session.teams.find((tm) => (tm.orgId ?? null) === (current?.id ?? null))?.id });
            }}
          >
            {t('nav.importDocs')}
          </Menu.Item>
        </Menu.Content>
      </Menu.Root>
      <Tip label={t('nav.hide')} keys={[MOD, '\\']}>
        <button className="icon-btn nav-hide" aria-label={t('nav.hide')} onClick={hideNav}>
          <PanelLeftClose size={18} />
        </button>
      </Tip>
      </div>

      <div className="nav-actions">
        <CreateMenu>
          <button className="btn primary nav-create">
            <Plus size={17} /> {t('create.title')}
          </button>
        </CreateMenu>
        <Tip label={t('quickOpen.button')} keys={[MOD, 'K']}>
          <button className="btn nav-search" aria-label={t('quickOpen.button')} onClick={() => openQuickOpen()}>
            <Search size={17} />
            <Kbd keys={[MOD, 'K']} />
          </button>
        </Tip>
      </div>

      <InboxNavItem active={active.kind === 'inbox'} />
      <Link to="/t" className="nav-item" data-active={active.kind === 'myIssues'}>
        <ListChecks size={17} />
        <span className="nav-label">{t('tracker.myIssues')}</span>
      </Link>
      <FavoritesSection active={active} />

      <div className="nav-section">
        <span>{t('spaces.title')}</span>
        <Menu.Root>
          <Menu.Trigger asChild>
            <button className="icon-btn sm nav-section-add" aria-label={t('spaces.add')}>
              <Plus size={14} />
            </button>
          </Menu.Trigger>
          <Menu.Content align="end">
            <Menu.Item icon={<FolderPlus size={16} />} onSelect={() => openCreate('space', { org, team: here?.team })}>
              {t('spaces.newSpace')}
            </Menu.Item>
            <Menu.Item icon={<ListPlus size={16} />} onSelect={() => openCreate('project', { org, team: here?.team ?? tree.groups[0]?.team.id ?? null, space: here?.space })}>
              {t('tracker.newProject')}
            </Menu.Item>
            {current && (
              <Menu.Item icon={<Users size={16} />} onSelect={() => openCreate('team', { org })}>
                {t('dashboard.newTeam')}
              </Menu.Item>
            )}
          </Menu.Content>
        </Menu.Root>
      </div>
      <div className="nav-tree">
        {spaces.isLoading && <Spinner />}
        {tree.groups.map((g) => {
          const id = `team:${g.team.id}`;
          const holds = g.spaces.some((s) => holdsSpace(s, active));
          const open = !tree.showTeams || branchTree.isOpen(id, holds, true);
          return (
            <div key={g.team.id} className="nav-team-group">
              {tree.showTeams && (
                <div className="nav-team-row">
                  <button className="nav-team-toggle" aria-expanded={open} aria-label={t(open ? 'spaces.collapse' : 'spaces.expand')} onClick={() => branchTree.toggle(id, open)}>
                    {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                  </button>
                  <TeamLink team={g.team} active={active.kind === 'team' && active.id === g.team.id && !teamSprints.data?.some((x) => x.teamId === g.team.id)} />
                </div>
              )}
              {open && <TeamSprintRows team={g.team.id} active={active} sprints={teamSprints.data ?? []} />}
              {open && g.spaces.map((s) => <SpaceRow key={s.space.id} entry={s} active={active} tree={branchTree} only={single} />)}
            </div>
          );
        })}
        {tree.others.length > 0 && (
          <div className="nav-team-group">
            <div className="nav-team static">
              <span className="nav-label">{t(org ? 'spaces.otherProjects' : 'spaces.guestProjects')}</span>
            </div>
            {tree.others.map((p) => (
              <ProjectRow key={p.id} p={p} active={active} />
            ))}
          </div>
        )}
        <ArchivedProjects active={active.kind === 'project' ? active.id : null} tree={branchTree} />
      </div>

      <div className="nav-bottom">
        <Link to={viewLink('all')} className="nav-item" data-active={isView('all', 'mine', 'shared', 'projects')}>
          {current ? <Building2 size={17} /> : <LayoutGrid size={17} />}
          <span className="nav-label">{t(current ? 'nav.everything' : 'nav.everythingPersonal')}</span>
        </Link>
        <Link to={viewLink('trash')} className="nav-item" data-active={isView('trash')}>
          <Trash2 size={17} />
          <span className="nav-label">{t('nav.trash')}</span>
        </Link>
      </div>

      <div className="side-footer">
        <UserMenu session={session} />
      </div>

      <CreateDialogs session={session} />
      <RenameHost />
      {importing && (
        <Suspense fallback={null}>
          <ImportDialog open={importing} onOpenChange={setImporting} session={session} />
        </Suspense>
      )}
      {docsImport && (
        <Suspense fallback={null}>
          <DocsImportDialog target={docsImport} session={session} onClose={closeDocsImport} />
        </Suspense>
      )}
      <NameDialog
        open={newOrg}
        onOpenChange={setNewOrg}
        title={t('org.createTitle')}
        description={t('org.createHint')}
        label={t('org.name')}
        placeholder={t('org.namePlaceholder')}
        submitLabel={t('common.create')}
        onSubmit={async (name) => {
          const res = await api.post<{ id: string }>('/orgs', { name });
          await refresh();
          switchOrg(res.id);
        }}
      />
    </aside>
  );
}

const holdsSpace = (entry: NavSpace, active: NavActive) =>
  (active.kind === 'space' && active.id === entry.space.id) ||
  (active.kind === 'project' && entry.projects.some((p) => p.id === active.id)) ||
  (active.kind === 'page' && active.spaceId === entry.space.id);

/** A chevron that opens a branch; an empty slot keeps the rows aligned where there is none. */
/** A team's heading: its name to its page, its "⋯" (its admins rename it there). */
function TeamLink({ team, active }: { team: { id: string; name: string; personal: boolean; role: string }; active: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const refresh = useRefreshSession();
  const menu = useRowMenu();
  const href = `/team/${team.id}`;
  const actions: RowAction[] =
    !team.personal && team.role !== 'member'
      ? [
          {
            icon: <Pencil size={16} />,
            label: t('nav.rename'),
            run: () =>
              openRename({
                title: t('teams.renameTitle'),
                label: t('teams.nameTitle'),
                initial: team.name,
                submit: async (name) => {
                  await api.patch(`/teams/${team.id}`, { name });
                  void refresh();
                  void qc.invalidateQueries({ queryKey: ['tracker'] });
                  void qc.invalidateQueries({ queryKey: ['spaces'] });
                },
              }),
          },
        ]
      : [];
  return (
    <Link to={href} className="nav-team" data-active={active} onContextMenu={menu.onContextMenu}>
      <span className="nav-label">{team.personal ? t('nav.personal') : team.name}</span>
      <NavRowMenu href={href} fav={{ kind: 'team', id: team.id }} actions={actions} open={menu.open} onOpenChange={menu.setOpen} />
    </Link>
  );
}

/** What the person pinned (docs/navigation-v2-plan.md): any kind, in their order, dragged to change it. */
function FavoritesSection({ active }: { active: NavActive }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const favs = useFavorites();
  const [drag, setDrag] = useState<string | null>(null);
  const [order, setOrder] = useState<Favorite[] | null>(null);
  const list = favs.data ?? [];
  if (!list.length) return null;
  const shown = order ?? list;
  const keyOf = (f: Favorite) => `${f.kind}:${f.id}`;
  const lit = (f: Favorite) =>
    (f.kind === 'project' && active.kind === 'project' && active.id === f.id) ||
    (f.kind === 'page' && active.kind === 'page' && active.id === f.id) ||
    (f.kind === 'space' && active.kind === 'space' && active.id === f.id) ||
    (f.kind === 'team' && active.kind === 'team' && active.id === f.id) ||
    (f.kind === 'filter' && active.kind === 'filter' && active.id === f.id);
  return (
    <>
      <div className="nav-section">
        <span>{t('favorites.title')}</span>
      </div>
      {shown.map((f) => (
        <FavoriteRow
          key={keyOf(f)}
          f={f}
          active={lit(f)}
          dragging={drag === keyOf(f)}
          onDragStart={() => setDrag(keyOf(f))}
          onDragOver={() => {
            if (!drag || drag === keyOf(f)) return;
            const from = shown.findIndex((x) => keyOf(x) === drag);
            const to = shown.findIndex((x) => keyOf(x) === keyOf(f));
            const next = [...shown];
            next.splice(to, 0, ...next.splice(from, 1));
            setOrder(next);
          }}
          onDrop={() => {
            if (order) void reorderFavorites(qc, order);
          }}
          onDragEnd={() => {
            setDrag(null);
            setOrder(null);
          }}
        />
      ))}
    </>
  );
}

function FavoriteRow({
  f,
  active,
  dragging,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: {
  f: Favorite;
  active: boolean;
  dragging: boolean;
  onDragStart: () => void;
  onDragOver: () => void;
  onDrop: () => void;
  onDragEnd: () => void;
}) {
  const { t } = useTranslation();
  const menu = useRowMenu();
  const href = favoriteLink(f);
  const icon =
    f.kind === 'board' ? (
      <LayoutDashboard size={16} />
    ) : f.kind === 'project' ? (
      <span className="key-chip">{f.key}</span>
    ) : f.kind === 'team' ? (
      <Users size={16} />
    ) : f.kind === 'filter' ? (
      <Bookmark size={16} />
    ) : f.icon ? (
      <span className="nav-space-icon">
        <SpaceIcon icon={f.icon} size={15} />
      </span>
    ) : f.kind === 'space' ? (
      <FolderClosed size={16} />
    ) : (
      <FileText size={16} />
    );
  return (
    <Link
      to={href}
      className="nav-item nav-favorite"
      data-active={active}
      data-dragging={dragging}
      draggable
      onContextMenu={menu.onContextMenu}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', f.title);
        onDragStart();
      }}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver();
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDrop();
      }}
      onDragEnd={onDragEnd}
    >
      {icon}
      <span className="nav-label">{f.title || (f.kind === 'team' && f.personal ? t('nav.personal') : t('docs.untitled'))}</span>
      <NavRowMenu href={href} fav={{ kind: f.kind, id: f.id }} open={menu.open} onOpenChange={menu.setOpen} />
    </Link>
  );
}

/** One "rename" dialog for every row of the menu. */
type Renaming = { title: string; label: string; initial: string; submit: (name: string) => Promise<void> };
const useRename = create<{ r: Renaming | null }>(() => ({ r: null }));
const openRename = (r: Renaming) => useRename.setState({ r });

function RenameHost() {
  const { t } = useTranslation();
  const r = useRename((s) => s.r);
  return (
    <NameDialog
      open={!!r}
      onOpenChange={(v) => !v && useRename.setState({ r: null })}
      title={r?.title ?? ''}
      label={r?.label ?? ''}
      initial={r?.initial ?? ''}
      submitLabel={t('common.save')}
      onSubmit={async (name) => {
        try {
          await r?.submit(name);
          useRename.setState({ r: null });
        } catch (e) {
          toast(errorText(e instanceof ApiError ? e.code : 'internal'));
        }
      }}
    />
  );
}

function Toggle({ open, onToggle, label }: { open: boolean; onToggle?: () => void; label: string }) {
  return (
    <button className="nav-toggle" aria-label={label} aria-expanded={onToggle ? open : undefined} disabled={!onToggle} onClick={onToggle}>
      {onToggle ? open ? <ChevronDown size={13} /> : <ChevronRight size={13} /> : null}
    </button>
  );
}

/**
 * A space: its own name (the default one included), and inside it "Boards" and its projects
 * (documents and diagrams to come). The deepest row in sight that matches the page is the one lit.
 */
function SpaceRow({ entry, active, tree, only }: { entry: NavSpace; active: NavActive; tree: Tree; only: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { space, projects } = entry;
  const onPage = active.kind === 'space' && active.id === space.id;
  const open = tree.isOpen(space.id, holdsSpace(entry, active), only);
  const menu = useRowMenu();
  const editor = space.role === 'admin' || space.role === 'editor';
  const fail = (e: unknown) => toast(errorText(e instanceof ApiError ? e.code : 'internal'));
  const actions: RowAction[] = [
    ...(editor
      ? [
          {
            icon: <LayoutDashboard size={16} />,
            label: t('nav.newBoard'),
            run: () =>
              void api.post<{ id: string }>('/boards', { teamId: space.teamId, spaceId: space.id }).then((b) => {
                void qc.invalidateQueries({ queryKey: ['boards'] });
                void qc.invalidateQueries({ queryKey: ['spaces'] });
                navigate(`/b/${b.id}`);
              }, fail),
          },
          {
            icon: <FileText size={16} />,
            label: t('nav.newPage'),
            run: () =>
              void createPage({ spaceId: space.id }).then((p) => {
                refreshPages(qc);
                navigate(pagePath(p.id));
              }, fail),
          },
          { icon: <ListPlus size={16} />, label: t('nav.newProject'), run: () => openCreate('project', { team: space.teamId, space: space.id }) },
        ]
      : []),
    ...(space.role === 'admin' ? [{ icon: <Pencil size={16} />, label: t('nav.rename'), run: () => openCreate('editSpace', { edit: space }) }] : []),
  ];
  return (
    <>
      <div className="nav-row">
        <Toggle open={open} onToggle={() => tree.toggle(space.id, open)} label={t(open ? 'spaces.collapse' : 'spaces.expand')} />
        <Link
          to={`/s/${space.id}`}
          className="nav-item nav-row-link"
          data-active={onPage && (!open || active.tab === 'tasks' || (active.tab === 'docs' && space.pages === 0))}
          onContextMenu={menu.onContextMenu}
        >
          {space.icon ? (
            <span className="nav-space-icon">
              <SpaceIcon icon={space.icon} size={16} />
            </span>
          ) : (
            <FolderClosed size={16} />
          )}
          <span className="nav-label">{space.name}</span>
          {space.private && <Lock size={12} className="nav-space-lock" aria-label={t('spaces.private')} />}
          <NavRowMenu href={`/s/${space.id}`} fav={{ kind: 'space', id: space.id }} actions={actions} open={menu.open} onOpenChange={menu.setOpen} />
        </Link>
      </div>
      {open && (
        <div className="nav-branch">
          <Link to={`/s/${space.id}`} className="nav-item nav-leaf" data-active={onPage && (active.tab ?? 'boards') === 'boards'}>
            <LayoutGrid size={15} />
            <span className="nav-label">{t('spaces.boards')}</span>
            {space.boards > 0 && <span className="count">{space.boards}</span>}
          </Link>
          {(space.pages > 0 || (active.kind === 'page' && active.spaceId === space.id)) && <DocsBranch space={space} active={active} tree={tree} />}
          {projects.map((p) => (
            <ProjectRow key={p.id} p={p} active={active} sharedFrom={p.spaceId !== space.id ? p.teams?.find((tm) => tm.home)?.name : undefined} />
          ))}
        </div>
      )}
    </>
  );
}

/** Dragging a page in the tree: which one, and where it would land on the row under the pointer. */
let dragged: { id: string; space: string } | null = null;
type Drop = 'before' | 'inside' | 'after';

/**
 * "Documents" of a space: its top pages, each opening to its children (loaded when opened). Editors
 * drag pages to reorder them or put one inside another, and add a page under any page.
 */
function DocsBranch({ space, active, tree }: { space: Space; active: NavActive; tree: Tree }) {
  const { t } = useTranslation();
  const id = `docs:${space.id}`;
  const onSpaceDocs = active.kind === 'space' && active.id === space.id && active.tab === 'docs';
  const holds = active.kind === 'page' && active.spaceId === space.id;
  const open = tree.isOpen(id, holds || onSpaceDocs);
  const editor = space.role === 'admin' || space.role === 'editor';
  return (
    <>
      <div className="nav-row">
        <Toggle open={open} onToggle={() => tree.toggle(id, open)} label={t(open ? 'spaces.collapse' : 'spaces.expand')} />
        <Link to={`/s/${space.id}?tab=docs`} className="nav-item nav-row-link nav-docs" data-active={onSpaceDocs}>
          <FileText size={15} />
          <span className="nav-label">{t('docs.title')}</span>
          {space.pages > 0 && <span className="count">{space.pages}</span>}
        </Link>
      </div>
      {open && (
        <div className="nav-branch">
          <PageLevel space={space.id} parent={null} active={active} tree={tree} editor={editor} />
        </div>
      )}
    </>
  );
}

function PageLevel({ space, parent, active, tree, editor }: { space: string; parent: string | null; active: NavActive; tree: Tree; editor: boolean }) {
  const { t } = useTranslation();
  const level = usePageLevel(space, parent);
  if (level.isLoading) return <div className="nav-pages-loading" />;
  const list = level.data ?? [];
  if (!list.length && parent === null) return <div className="nav-empty muted">{t('docs.noPages')}</div>;
  return (
    <>
      {list.map((p, i) => (
        <PageNode key={p.id} page={p} prev={list[i - 1]?.id ?? null} next={list[i + 1]?.id ?? null} active={active} tree={tree} editor={editor} />
      ))}
    </>
  );
}

function PageNode({ page, prev, next, active, tree, editor }: { page: Page; prev: string | null; next: string | null; active: NavActive; tree: Tree; editor: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [drop, setDrop] = useState<Drop | null>(null);
  const here = active.kind === 'page' && active.id === page.id;
  const holds = active.kind === 'page' && active.path.includes(page.id) && !here;
  const id = `pg:${page.id}`;
  const open = page.children > 0 && tree.isOpen(id, holds);
  const fail = (e: unknown) => toast(errorText(e instanceof ApiError ? e.code : 'internal'));
  const where = (e: React.DragEvent): Drop => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const y = (e.clientY - r.top) / r.height;
    return y < 0.28 ? 'before' : y > 0.72 ? 'after' : 'inside';
  };
  const land = async (to: Drop) => {
    const d = dragged;
    dragged = null;
    if (!d || d.id === page.id || d.space !== page.spaceId) return;
    try {
      if (to === 'inside') await movePage(d.id, { parentId: page.id });
      else await movePage(d.id, { parentId: page.parentId, beforeId: to === 'before' ? page.id : next, afterId: to === 'before' ? prev : page.id });
      refreshPages(qc);
      if (to === 'inside' && !open) tree.toggle(id, false);
    } catch (e) {
      fail(e);
    }
  };
  const menu = useRowMenu();
  const actions: RowAction[] = editor
    ? [
        { icon: <Plus size={16} />, label: t('docs.addChild'), run: () => void addChild() },
        {
          icon: <Pencil size={16} />,
          label: t('nav.rename'),
          run: () =>
            openRename({
              title: t('nav.renamePage'),
              label: t('docs.titleLabel'),
              initial: page.title,
              submit: async (title) => {
                await api.patch(`/pages/${page.id}`, { title });
                refreshPages(qc);
              },
            }),
        },
      ]
    : [];
  const addChild = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    try {
      const child = await createPage({ parentId: page.id });
      refreshPages(qc);
      if (!open) tree.toggle(id, false);
      navigate(pagePath(child.id));
    } catch (err) {
      fail(err);
    }
  };
  return (
    <>
      <div
        className="nav-row nav-page"
        data-drop={drop ?? undefined}
        draggable={editor}
        onDragStart={(e) => {
          dragged = { id: page.id, space: page.spaceId };
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', page.title);
        }}
        onDragEnd={() => {
          dragged = null;
          setDrop(null);
        }}
        onDragOver={(e) => {
          if (!dragged || dragged.id === page.id) return;
          e.preventDefault();
          setDrop(where(e));
        }}
        onDragLeave={() => setDrop(null)}
        onDrop={(e) => {
          e.preventDefault();
          const to = where(e);
          setDrop(null);
          void land(to);
        }}
      >
        <Toggle open={open} onToggle={page.children > 0 ? () => tree.toggle(id, open) : undefined} label={t(open ? 'spaces.collapse' : 'spaces.expand')} />
        <Link to={pagePath(page.id)} className="nav-item nav-row-link" data-active={here || (holds && !open)} draggable={false} onContextMenu={menu.onContextMenu}>
          {page.icon ? (
            <span className="nav-space-icon">
              <SpaceIcon icon={page.icon} size={15} />
            </span>
          ) : (
            <FileText size={15} />
          )}
          <span className="nav-label">{pageTitle(page, t('docs.untitled'))}</span>
          <ApprovalBadge state={page.approval} size={12} />
          {editor && (
            <button type="button" className="nav-page-add" aria-label={t('docs.addChild')} title={t('docs.addChild')} onClick={(e) => void addChild(e)}>
              <Plus size={13} />
            </button>
          )}
          <NavRowMenu href={pagePath(page.id)} fav={{ kind: 'page', id: page.id }} actions={actions} open={menu.open} onOpenChange={menu.setOpen} />
        </Link>
      </div>
      {open && (
        <div className="nav-branch">
          <PageLevel space={page.spaceId} parent={page.id} active={active} tree={tree} editor={editor} />
        </div>
      )}
    </>
  );
}

/** Where a project's rows lead: its board (with the running sprint), backlog and sprints, reports. */
/**
 * A project: one row to its last opened view (the board, backlog and reports are tabs on its page).
 * `sharedFrom`: the project is another team's (its name), and this team works in it.
 */
function ProjectRow({ p, active, sharedFrom }: { p: TrackerProject; active: NavActive; sharedFrom?: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const menu = useRowMenu();
  const actions: RowAction[] =
    p.role === 'owner'
      ? [
          {
            icon: <Pencil size={16} />,
            label: t('nav.rename'),
            run: () =>
              openRename({
                title: t('nav.renameProject'),
                label: t('tracker.projectName'),
                initial: p.name,
                submit: async (name) => {
                  await api.patch(`/tracker/projects/${p.id}`, { name });
                  void qc.invalidateQueries({ queryKey: ['tracker'] });
                },
              }),
          },
        ]
      : [];
  return (
    <Link to={projectLink(p.id)} className="nav-item nav-leaf" data-active={active.kind === 'project' && active.id === p.id} onContextMenu={menu.onContextMenu}>
      <span className="key-chip">{p.key}</span>
      <span className="nav-label">{p.name}</span>
      {sharedFrom && (
        <span className="nav-shared" title={t('spaces.sharedFrom', { team: sharedFrom })} aria-label={t('spaces.sharedFrom', { team: sharedFrom })}>
          <Users size={13} />
        </span>
      )}
      <NavRowMenu href={`/t/${p.id}`} fav={{ kind: 'project', id: p.id }} actions={actions} open={menu.open} onOpenChange={menu.setOpen} />
    </Link>
  );
}

/** A team that plans work (a team in projects, or one keeping sprints): its sprint board, with the running sprint, and backlog. */
function TeamSprintRows({ team, active, sprints }: { team: string; active: NavActive; sprints: { teamId: string; active: string | null }[] }) {
  const { t } = useTranslation();
  const mine = sprints.find((s) => s.teamId === team);
  if (!mine) return null;
  const here = active.kind === 'team' && active.id === team;
  const tab = here ? (active.tab ?? 'board') : null;
  return (
    <>
      <Link to={`/team/${team}`} className="nav-item nav-leaf" data-active={tab === 'board'}>
        <Columns3 size={15} />
        <span className="nav-label">
          {t('teams.tab.board')}
          {mine.active && <span className="nav-sprint"> · {mine.active}</span>}
        </span>
      </Link>
      <Link to={`/team/${team}?tab=backlog`} className="nav-item nav-leaf" data-active={tab === 'backlog'}>
        <Rows3 size={15} />
        <span className="nav-label">{t('tracker.view.backlog')}</span>
      </Link>
    </>
  );
}

/** Archived projects, folded away: an owner opens one to bring it back. */
function ArchivedProjects({ active, tree }: { active: string | null; tree: Tree }) {
  const { t } = useTranslation();
  const archived = useArchivedProjects();
  const list = archived.data ?? [];
  if (!list.length) return null;
  const holds = list.some((p) => p.id === active);
  const open = tree.isOpen('archived', holds);
  return (
    <div className="nav-team-group">
      <button className="nav-team" aria-expanded={open} onClick={() => tree.toggle('archived', open)}>
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        <Archive size={13} />
        <span className="nav-label">{t('trackerSettings.archiveNav', { count: list.length })}</span>
      </button>
      {open &&
        list.map((p) => (
          <Link key={p.id} to={`/t/${p.id}`} className="nav-item nav-archived" data-active={active === p.id}>
            <span className="key-chip">{p.key}</span>
            <span className="nav-label">{p.name}</span>
          </Link>
        ))}
    </div>
  );
}

